import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  adminService,
  DashboardStats,
  StatWithChange,
  MonthlyGrowthResponse,
  DailyStats,
  AdminMrrResponse,
  AdminMrrShopRow,
  AdminMrrShopStatus,
} from "../../services/admin/admin.service";
import {
  Users,
  Building2,
  DollarSign,
  TrendingUp,
  Wallet,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { cn } from "../../lib/utils";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";

// Skeleton loading components
function StatCardSkeleton() {
  return (
    <Card className="animate-pulse border-border bg-transparent">
      <CardContent className="p-6">
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2 flex-1">
            <div className="h-3 bg-muted rounded w-1/2"></div>
            <div className="h-8 bg-muted rounded w-1/3"></div>
          </div>
          <div className="h-12 w-12 rounded-2xl border border-border bg-transparent"></div>
        </div>
      </CardContent>
    </Card>
  );
}

// Add interface for chart data
interface ChartDataPoint {
  date: string;
  affiliatesJoined: number;
  brandsJoined: number;
  salesAmount: number;
  salesCount: number;
}

// Enhanced stat card component using shadcn Card
interface StatCardProps {
  title: string;
  stat: StatWithChange;
  icon: React.ReactNode;
  variant?: "warm" | "neutral";
  formatter?: (value: number) => string;
}

function StatCard({
  title,
  stat,
  icon,
  variant = "neutral",
  formatter = (v) => v.toString(),
}: StatCardProps) {
  const isWarm = variant === "warm";

  return (
    <Card
      className={cn(
        "transition-all duration-300 border-border bg-transparent",
        isWarm && "hover:border-[#d7785b]/40"
      )}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2 flex-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {title}
            </p>
            <p className="text-3xl font-bold text-foreground">
              {formatter(stat.current)}
            </p>
          </div>
          <div
            className={cn(
              "h-12 w-12 rounded-2xl flex items-center justify-center border",
              isWarm
                ? "border-[#d7785b]/20 bg-transparent"
                : "border-border bg-transparent"
            )}
          >
            <div className={isWarm ? "text-[#d7785b]" : "text-muted-foreground"}>
              {icon}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function AdminDashboard() {
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>("30");
  const [monthlyGrowthData, setMonthlyGrowthData] =
    useState<MonthlyGrowthResponse | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [chartLoading, setChartLoading] = useState(false);
  const [mrr, setMrr] = useState<AdminMrrResponse | null>(null);
  const [mrrLoading, setMrrLoading] = useState(true);
  const [mrrError, setMrrError] = useState<string | null>(null);
  const [mrrFilter, setMrrFilter] = useState<MrrFilter>("attention");

  // Live MRR: the server reconciles DB subscriptions against Shopify billing
  // and caches the answer for 6h. Refresh re-queries every shop.
  const fetchMrr = async (refresh = false) => {
    setMrrLoading(true);
    setMrrError(null);
    try {
      setMrr(await adminService.getMrr(refresh));
    } catch (error) {
      setMrrError(
        error instanceof Error ? error.message : "Failed to fetch MRR"
      );
    } finally {
      setMrrLoading(false);
    }
  };

  const fetchStats = async (timeframe: string = "30") => {
    setIsLoading(true);
    try {
      const data = await adminService.getDashboardStats(timeframe);
      setStats(data);
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimeframeChange = (timeframe: string) => {
    setSelectedTimeframe(timeframe);
    fetchStats(timeframe);
  };

  // Generate month options from April 2025 to current month
  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // 0-indexed, so add 1

    // Start from April 2025
    let year = 2025;
    let month = 4;

    while (
      year < currentYear ||
      (year === currentYear && month <= currentMonth)
    ) {
      const monthName = new Date(year, month - 1).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
      options.push({
        value: `${year}-${month}`,
        label: monthName,
      });

      month++;
      if (month > 12) {
        month = 1;
        year++;
      }
    }

    return options.reverse(); // Show newest first
  };

  const fetchMonthlyGrowth = async (yearMonth: string) => {
    if (!yearMonth) return;

    setChartLoading(true);
    try {
      const [year, month] = yearMonth.split("-").map(Number);
      const data = await adminService.getMonthlyGrowth(year, month);
      setMonthlyGrowthData(data);

      // Transform data for charts
      const transformedData: ChartDataPoint[] = data.dailyStats.map(
        (day: DailyStats) => ({
          date: `${month}/${day.day}`,
          affiliatesJoined: day.affiliates,
          brandsJoined: day.brands,
          salesAmount: day.revenue,
          salesCount: day.orders,
        })
      );

      setChartData(transformedData);
    } catch (error) {
      console.error("Failed to fetch monthly growth:", error);
    } finally {
      setChartLoading(false);
    }
  };

  const handleMonthChange = (yearMonth: string) => {
    setSelectedMonth(yearMonth);
    fetchMonthlyGrowth(yearMonth);
  };

  useEffect(() => {
    fetchStats();
    fetchMrr();

    // Set default month to current month
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const defaultMonth = `${currentYear}-${currentMonth}`;
    setSelectedMonth(defaultMonth);
    fetchMonthlyGrowth(defaultMonth);
  }, []);

  useEffect(() => {
    fetchStats(selectedTimeframe);
  }, [selectedTimeframe]);

  /** Every bucket a brand can land in, in the order the breakdown shows them. */
const MRR_BUCKETS: Array<{
  status: AdminMrrShopStatus;
  label: string;
  hint: string;
  /** Never sold: DB amount is shown but excluded from DB MRR. */
  excluded?: boolean;
}> = [
  {
    status: "billing",
    label: "Paying",
    hint: "Shopify confirms a recurring charge past its trial",
  },
  { status: "trial", label: "In trial", hint: "Active on Shopify, not billed yet" },
  {
    status: "free",
    label: "Free",
    hint: "Installed, no recurring charge on either side",
  },
  {
    status: "mismatch",
    label: "Mismatch",
    hint: "DB and Shopify disagree on what is billed",
  },
  {
    status: "unreachable",
    label: "Unverified",
    hint: "Shopify could not confirm the plan; DB amount left out of live MRR",
  },
  {
    status: "uninstalled",
    label: "Uninstalled",
    hint: "App removed, no paid plan left in the DB",
    excluded: true,
  },
  {
    status: "test",
    label: "Test",
    hint: "Dev stores, test accounts and Shopify test charges",
    excluded: true,
  },
];

type MrrFilter = AdminMrrShopStatus | "attention" | "all";

const MRR_STATUS_CLASS: Record<AdminMrrShopStatus, string> = {
  billing: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  trial: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  free: "bg-muted text-muted-foreground",
  mismatch: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  unreachable: "bg-red-500/15 text-red-700 dark:text-red-300",
  uninstalled: "bg-muted text-muted-foreground",
  test: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
};

function MrrStatusBadge({ status }: { status: AdminMrrShopStatus }) {
  const label = MRR_BUCKETS.find((b) => b.status === status)?.label ?? status;
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        MRR_STATUS_CLASS[status],
      )}
    >
      {label}
    </span>
  );
}

function mrrRowsFor(rows: AdminMrrShopRow[], filter: MrrFilter) {
  if (filter === "all") return rows;
  if (filter === "attention") {
    return rows.filter((r) => r.status === "mismatch" || r.status === "unreachable");
  }
  return rows.filter((r) => r.status === filter);
}

function mrrFilterLabel(filter: MrrFilter) {
  if (filter === "all") return "All brands";
  if (filter === "attention") return "To review";
  return MRR_BUCKETS.find((b) => b.status === filter)?.label ?? filter;
}

/**
 * Who a reconciliation row belongs to: brand name linking to the admin Brands
 * page pre-filtered on that brand, then login email and shop domain.
 */
function MrrBrandIdentity({ row }: { row: AdminMrrShopRow }) {
  const name = row.companyName || row.shop;
  const search = row.companyName ?? row.shop;
  return (
    <div className="min-w-0">
      <Link
        to={`/admin/brands?search=${encodeURIComponent(search)}`}
        className="font-medium text-foreground hover:underline"
      >
        {name}
      </Link>
      {row.email && (
        <div className="text-xs text-muted-foreground truncate">{row.email}</div>
      )}
      {row.companyName && (
        <div className="text-xs text-muted-foreground truncate">{row.shop}</div>
      )}
    </div>
  );
}

const formatCurrency = (value: number) =>
    `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-8 max-w-7xl mx-auto p-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-2">
        <div className="space-y-1">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
            Dashboard
          </h1>
          <p className="text-muted-foreground text-lg">
            Welcome Back! Here's what's happening at Reveshare.
          </p>
        </div>

        {/* Stats Period Selector */}
        <div className="flex items-center gap-2 bg-transparent rounded-xl border border-border px-4 py-2.5 transition-all duration-300 hover:border-[#d7785b]/40">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground text-sm font-medium">Period:</span>
          <Select
            value={selectedTimeframe}
            onValueChange={handleTimeframeChange}
          >
            <SelectTrigger className="w-[140px] border-0 bg-transparent focus:ring-0 text-sm font-medium">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="90">Last 90 Days</SelectItem>
              <SelectItem value="365">Last 365 Days</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              title="Total Brands"
              stat={{
                current: stats?.metrics.totalBrands || 0,
                previous: 0,
                percentageChange: 0,
              }}
              icon={<Building2 className="h-5 w-5" />}
              variant="neutral"
            />
            <StatCard
              title="Active Brands"
              stat={{
                current: stats?.metrics.activeBrands || 0,
                previous: 0,
                percentageChange: 0,
              }}
              icon={<Building2 className="h-5 w-5" />}
              variant="warm"
            />
            <StatCard
              title="Total Sales"
              stat={{
                current: stats?.metrics.totalSales || 0,
                previous: 0,
                percentageChange: 0,
              }}
              icon={<Users className="h-5 w-5" />}
              variant="neutral"
            />
            <StatCard
              title="Average Sale Value"
              stat={{
                current: stats?.metrics.averageSaleValue || 0,
                previous: 0,
                percentageChange: 0,
              }}
              icon={<DollarSign className="h-5 w-5" />}
              variant="warm"
              formatter={formatCurrency}
            />
            <StatCard
              title="Total Sales Amount"
              stat={{
                current: stats?.metrics.totalSalesAmount || 0,
                previous: 0,
                percentageChange: 0,
              }}
              icon={<DollarSign className="h-5 w-5" />}
              variant="neutral"
              formatter={formatCurrency}
            />
          </>
        )}
        {mrrLoading && !mrr ? (
          <StatCardSkeleton />
        ) : (
          <StatCard
            title="MRR (Shopify live)"
            stat={{
              current: mrr?.totals.liveMrr || 0,
              previous: 0,
              percentageChange: 0,
            }}
            icon={<Wallet className="h-5 w-5" />}
            variant="warm"
            formatter={formatCurrency}
          />
        )}
      </div>

      {/* Subscription reconciliation: DB flag vs what Shopify actually bills */}
      <Card className="border-border bg-transparent">
        <CardContent className="p-6 space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
            <div className="space-y-1">
              <h2 className="text-xl font-semibold text-foreground">
                Subscription reconciliation
              </h2>
              <p className="text-sm text-muted-foreground">
                Live MRR counts recurring charges Shopify is billing right now,
                on non-test subscriptions past their trial. Payouts land later
                and net of Shopify&apos;s share, so they will not match this
                figure to the dollar.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {mrr && (
                <span className="text-xs text-muted-foreground">
                  {mrr.fromCache ? "Cached " : "Checked "}
                  {new Date(mrr.computedAt).toLocaleString()}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchMrr(true)}
                disabled={mrrLoading}
              >
                <RefreshCw
                  className={cn("h-3.5 w-3.5 mr-1.5", mrrLoading && "animate-spin")}
                />
                Re-check Shopify
              </Button>
            </div>
          </div>

          {mrrError && (
            <p className="text-sm text-destructive">{mrrError}</p>
          )}

          {mrr && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                {[
                  { label: "Live MRR", value: formatCurrency(mrr.totals.liveMrr) },
                  { label: "DB MRR", value: formatCurrency(mrr.totals.dbMrr) },
                  {
                    label: "Difference",
                    value: formatCurrency(mrr.totals.dbMrr - mrr.totals.liveMrr),
                  },
                  { label: "In trial", value: formatCurrency(mrr.totals.trialMrr) },
                  {
                    label: "Usage this period",
                    value: formatCurrency(mrr.totals.usageThisPeriod),
                  },
                  { label: "ARR run rate", value: formatCurrency(mrr.totals.arrRunRate) },
                ].map((item) => (
                  <div key={item.label} className="space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {item.label}
                    </p>
                    <p className="text-lg font-semibold text-foreground">{item.value}</p>
                  </div>
                ))}
              </div>

              {/* Where every brand that ever installed sits right now */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-foreground">Brand breakdown</h3>
                <p className="text-xs text-muted-foreground">
                  Every shop that ever installed the app, in exactly one bucket.
                  Click a bucket to list its brands. Uninstalled and test rows
                  are shown but kept out of DB MRR.
                </p>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Bucket</TableHead>
                        <TableHead className="text-right">Brands</TableHead>
                        <TableHead className="text-right">DB MRR</TableHead>
                        <TableHead className="text-right">Shopify MRR</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {MRR_BUCKETS.map((bucket) => {
                        const rows = mrr.shops.filter((r) => r.status === bucket.status);
                        const db = rows.reduce((sum, r) => sum + r.dbMrr, 0);
                        const live = rows.reduce((sum, r) => sum + r.shopifyMrr, 0);
                        const selected = mrrFilter === bucket.status;
                        return (
                          <TableRow
                            key={bucket.status}
                            onClick={() => setMrrFilter(selected ? "attention" : bucket.status)}
                            className={cn("cursor-pointer", selected && "bg-muted/60")}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <MrrStatusBadge status={bucket.status} />
                                <span className="text-xs text-muted-foreground">{bucket.hint}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {mrr.counts[bucket.status]}
                            </TableCell>
                            <TableCell
                              className={cn("text-right", bucket.excluded && "text-muted-foreground")}
                            >
                              {formatCurrency(db)}
                            </TableCell>
                            <TableCell className="text-right">
                              {bucket.status === "unreachable" ? "unknown" : formatCurrency(live)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow className="font-semibold bg-muted/30 hover:bg-muted/30">
                        <TableCell>Total</TableCell>
                        <TableCell className="text-right">{mrr.counts.totalBrands}</TableCell>
                        <TableCell className="text-right">{formatCurrency(mrr.totals.dbMrr)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(mrr.totals.liveMrr)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                <p className="text-xs text-muted-foreground">
                  {mrr.counts.shopsChecked} of {mrr.counts.totalBrands} shops were asked on
                  Shopify. The total row is DB MRR and live MRR as reported above, which leave
                  out the uninstalled and test buckets.
                </p>
              </div>

              {/* One table, filtered by the bucket picked above */}
              {(() => {
                const rows = mrrRowsFor(mrr.shops, mrrFilter);
                return (
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-sm font-semibold text-foreground">
                        {mrrFilterLabel(mrrFilter)} ({rows.length})
                      </h3>
                      <div className="flex gap-2">
                        <Button
                          variant={mrrFilter === "attention" ? "secondary" : "ghost"}
                          size="sm"
                          onClick={() => setMrrFilter("attention")}
                        >
                          To review
                        </Button>
                        <Button
                          variant={mrrFilter === "all" ? "secondary" : "ghost"}
                          size="sm"
                          onClick={() => setMrrFilter("all")}
                        >
                          All brands
                        </Button>
                      </div>
                    </div>
                    {rows.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No brands in this bucket.
                      </p>
                    ) : (
                      <div className="max-h-[21rem] overflow-y-auto rounded-md border [&>div]:overflow-visible">
                        <Table>
                          <TableHeader className="sticky top-0 z-10 bg-card shadow-[inset_0_-1px_0_hsl(var(--border))]">
                            <TableRow>
                              <TableHead>Brand</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>DB plan</TableHead>
                              <TableHead className="text-right">DB</TableHead>
                              <TableHead>Shopify plan</TableHead>
                              <TableHead className="text-right">Shopify</TableHead>
                              <TableHead>Why</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {rows.map((row) => (
                              <TableRow key={row.sessionId}>
                                <TableCell>
                                  <MrrBrandIdentity row={row} />
                                </TableCell>
                                <TableCell>
                                  <MrrStatusBadge status={row.status} />
                                </TableCell>
                                <TableCell>
                                  {row.dbPlan ?? "none"}
                                  {row.dbAmpedPlan ? ` + ${row.dbAmpedPlan}` : ""}
                                </TableCell>
                                <TableCell className="text-right">{formatCurrency(row.dbMrr)}</TableCell>
                                <TableCell>{row.shopifyPlan ?? "none"}</TableCell>
                                <TableCell className="text-right">
                                  {row.status === "unreachable" || row.status === "uninstalled"
                                    ? "-"
                                    : formatCurrency(row.shopifyMrr)}
                                </TableCell>
                                <TableCell
                                  className="text-xs text-muted-foreground max-w-xs"
                                  title={row.detail ?? undefined}
                                >
                                  {row.reason}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </CardContent>
      </Card>

      {/* Growth Analytics Section */}
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-2">
          <div className="space-y-1">
            <h2 className="text-3xl font-bold text-foreground">
              Growth Analytics
            </h2>
            <p className="text-muted-foreground">
              Track daily growth metrics and performance trends
            </p>
          </div>
          <div className="flex items-center gap-2 bg-transparent rounded-xl border border-border px-4 py-2.5 transition-all duration-300 hover:border-[#d7785b]/40">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground text-sm font-medium">Month:</span>
            <Select value={selectedMonth} onValueChange={handleMonthChange}>
              <SelectTrigger className="w-[140px] border-0 bg-transparent focus:ring-0 text-sm font-medium">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {generateMonthOptions().map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* User Growth Chart */}
          <Card className="p-8 bg-transparent border-border transition-all duration-300 hover:border-[#d7785b]/40">
            <CardContent className="p-0">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-1">
                    User Growth
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Daily new registrations for selected month
                  </p>
                  {monthlyGrowthData && (
                    <p className="text-muted-foreground text-xs mt-1">
                      Total: {monthlyGrowthData.monthlyTotals.totalBrands}{" "}
                      brands, {monthlyGrowthData.monthlyTotals.totalAffiliates}{" "}
                      affiliates
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-6 text-sm">
                  <div className="flex items-center">
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: "#d7785b" }}
                    ></div>
                    <span className="text-muted-foreground font-medium">
                      Affiliates
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: "#a0616a" }}
                    ></div>
                    <span className="text-muted-foreground font-medium">Brands</span>
                  </div>
                </div>
              </div>
              {chartLoading ? (
                <div className="flex items-center justify-center h-80">
                  <div
                    className="animate-spin rounded-full h-12 w-12 border-b-2"
                    style={{ borderColor: "#d7785b" }}
                  ></div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="affiliateGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#d7785b"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#d7785b"
                          stopOpacity={0}
                        />
                      </linearGradient>
                      <linearGradient
                        id="brandGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#a0616a"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#a0616a"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#78716c" }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#78716c" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fafaf9",
                        border: "1px solid #e7e5e4",
                        borderRadius: "12px",
                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="affiliatesJoined"
                      stroke="#d7785b"
                      strokeWidth={3}
                      fill="url(#affiliateGradient)"
                      name="Affiliates Joined"
                    />
                    <Area
                      type="monotone"
                      dataKey="brandsJoined"
                      stroke="#a0616a"
                      strokeWidth={3}
                      fill="url(#brandGradient)"
                      name="Brands Joined"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Sales Chart */}
          <Card className="p-8 bg-transparent border-border transition-all duration-300 hover:border-[#d7785b]/40">
            <CardContent className="p-0">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-1">
                    Sales Performance
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Daily revenue and orders for selected month
                  </p>
                  {monthlyGrowthData && (
                    <p className="text-muted-foreground text-xs mt-1">
                      Total:{" "}
                      {formatCurrency(
                        monthlyGrowthData.monthlyTotals.totalRevenue
                      )}{" "}
                      revenue, {monthlyGrowthData.monthlyTotals.totalOrders}{" "}
                      orders
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-6 text-sm">
                  <div className="flex items-center">
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: "#c17b63" }}
                    ></div>
                    <span className="text-muted-foreground font-medium">Revenue</span>
                  </div>
                  <div className="flex items-center">
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: "#8b5a3c" }}
                    ></div>
                    <span className="text-muted-foreground font-medium">Orders</span>
                  </div>
                </div>
              </div>
              {chartLoading ? (
                <div className="flex items-center justify-center h-80">
                  <div
                    className="animate-spin rounded-full h-12 w-12 border-b-2"
                    style={{ borderColor: "#d7785b" }}
                  ></div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="salesGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#c17b63"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#c17b63"
                          stopOpacity={0}
                        />
                      </linearGradient>
                      <linearGradient
                        id="countGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#8b5a3c"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#8b5a3c"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#78716c" }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#78716c" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fafaf9",
                        border: "1px solid #e7e5e4",
                        borderRadius: "12px",
                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                      }}
                      formatter={(value, name) => [
                        name === "Sales Amount"
                          ? formatCurrency(value as number)
                          : value,
                        name,
                      ]}
                    />
                    <Area
                      type="monotone"
                      dataKey="salesAmount"
                      stroke="#c17b63"
                      strokeWidth={3}
                      fill="url(#salesGradient)"
                      name="Sales Amount"
                    />
                    <Area
                      type="monotone"
                      dataKey="salesCount"
                      stroke="#8b5a3c"
                      strokeWidth={3}
                      fill="url(#countGradient)"
                      name="Sales Count"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
